import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  ALTERNATIVE_OFFER_SHOW_MESSAGE_IDKEY,
  ALTERNATIVEOFFERIDKEY,
  CMS_CONFIG,
  CONSUMER_CMS_SHOPPING_CART,
  convertMbToGb,
  deleteIsStartFromLockin,
  deleteStartFromLockinId,
  getCoverageToolFlowType,
  getFWTechByName,
  getRedirectCoverage,
  getSelectedOfferIds,
  ICommonLabelsInStepper,
  IGlobalConfigCMS,
  IProduct,
  ITechnologiesFastwebCMS,
  organizeCommonLabelsInStepper,
  setHubPath,
  setIsStartFromLockIn,
  setStartFromLockInId,
  useCmsConfig,
  usePage,
} from '@vfit/consumer/data-access';
import {
  API,
  AppMobile,
  checkWindow,
  ErrorDetail,
  errorManager,
  ErrorService,
  LoggerInstance,
  PAGES,
  trackView,
} from '@vfit/shared/data-access';
import { StepperStateCards } from '@vfit/shared/components';
import { CoverageErrorType, IUpdateProductPriceType } from '@vfit/shared/models';
import { updateProductPrice } from '@vfit/consumer/ibuyfixed';
import { ICoverageToolModalProps } from './coverageToolModal.models';
import { CoverageToolModalContainer } from './coverageToolModal.style';
import { useCoverageToolModalProvider } from './coverageToolModal.context';
import { ENABLE_DEBUG, getCoverageToolFlow, organizeFlowsAppend } from './coverageToolModal.utils';
import { ID_FLOWS, MANUAL_FLOW } from './coverageToolModal.flow';
import { IStepperUtils } from '../StepperStateCards/stepperStateCards.models';
import { useCoverageToolFlow } from './hooks/useCoverageToolFlow';
import EnableDebug from './components/Debug/enableDebug';
import { offersProposalManager } from '@vfit/consumer/providers';
import { HiddenPostForm, IBlackBoxCMS } from '@vfit/consumer/components';
import { useQueryClient } from 'react-query';
import { removeCurrency } from '@vfit/consumer/hooks';

const CoverageToolModalContent = ({
  handleClose,
  backgroundImage,
  product,
  customRedirect,
}: ICoverageToolModalProps) => {
  const queryClient = useQueryClient();
  const stepperRef = useRef<IStepperUtils>();
  const {
    address,
    isManual,
    slidesGoBack,
    isDisabledBackButton,
    setOfferId,
    setSlidesGoBack,
    setProduct,
    setIsLoading,
    setIsSuccess,
    setIsError,
    setErrorCoverage,
    setIsAddressConfirmed,
    setCoverageToolFlowType,
  } = useCoverageToolModalProvider();
  const [steps, setSteps] = useState(getCoverageToolFlow(product?.isLockInProduct));
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [postForm, setPostForm] = useState<{
    isEnabled: boolean;
    url: string;
    params: {
      [key: string]: {
        name: string;
        value: string;
      };
    };
  }>({
    isEnabled: false,
    url: process?.env?.['NEXT_PUBLIC_FASTWEB_POST_COVERAGE'] || '',
    params: {},
  });
  const { isSuccess, isError, isLoading, errorType, bestTech } = useCoverageToolFlow();
  const { products: allProducts } = usePage();
  const { pathname, reload } = useRouter();
  const nodeRef = useRef(null);
  const commonLabels = useCmsConfig(
    CMS_CONFIG[PAGES.CONSUMER],
    API.CMS_GET_STEPPER_COMMON_LABELS_FIXED
  ) as ICommonLabelsInStepper;
  const { blackbox } = (queryClient?.getQueryData('getBestOfferVfFw') || {}) as IBlackBoxCMS;
  const technologiesFastwebCMS = (queryClient?.getQueryData('getTechnologiesFastweb') ||
    {}) as ITechnologiesFastwebCMS;
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const { goBackInStepper } = organizeCommonLabelsInStepper(commonLabels);

  const programmaticallyGoBackNext = async (numberSlides: number, type: 'NEXT' | 'BACK') => {
    const delay = (time: number) => new Promise((resolve) => setTimeout(resolve, time));
    if (numberSlides !== -1) {
      // eslint-disable-next-line no-restricted-syntax, @typescript-eslint/no-unused-vars
      for (const indexSlides of Array.from(Array(numberSlides))) {
        if (type === 'NEXT') {
          stepperRef?.current?._stepperGoNext();
          await delay(100);
        } else {
          stepperRef?.current?._stepperGoPrev();
          await delay(600);
        }
      }
      setSlidesGoBack(-1);
    }
  };

  const alternativeProductShowMessageInMap = product?.alternativeProductShowMessageInMap || [];

  const hideBackButton = () =>
    (AppMobile.checkIsApp() || product?.disableBackOnCoverage) && activeSlideIndex === 0;

  const handleRedirect = () => {
    const { offerId: filterdOfferId, cmsId } = getSelectedOfferIds();
    const isAlternativeOffer: boolean = product?.offerId != filterdOfferId;
    const paths = pathname.split('/', -1);
    const isShoppingCart = paths[paths.length - 1] === PAGES.SHOPPING_CART;
    let productRedirect: IProduct | undefined = {
      ...(product as IProduct),
      ...updateProductPrice(product as IProduct, IUpdateProductPriceType.EDIT),
    };
    localStorage.removeItem(ALTERNATIVEOFFERIDKEY);
    localStorage.removeItem(ALTERNATIVE_OFFER_SHOW_MESSAGE_IDKEY);
    if (isAlternativeOffer && !AppMobile.checkIsApp() && !isShoppingCart) {
      productRedirect = allProducts?.find((productForSlug) => productForSlug?.cmsId == cmsId);
      if (productRedirect) {
        localStorage.setItem('tProd', JSON.stringify(productRedirect));
        localStorage.setItem(ALTERNATIVEOFFERIDKEY, '1');
        if (productRedirect?.cmsId) {
          const showMessageInMap =
            alternativeProductShowMessageInMap?.includes(productRedirect.cmsId.toString()) || false;
          if (showMessageInMap) localStorage.setItem(ALTERNATIVE_OFFER_SHOW_MESSAGE_IDKEY, '1');
        }
      }
    }
    if (!productRedirect) {
      LoggerInstance.debug('Non siamo riusciti a trovare il prodotto');
      const errorGeneric: ErrorDetail = {
        message: 'Si è verificato un errore.',
        title: 'Ops',
        actionEvent: () => {
          if (checkWindow()) {
            window.location.href = '/';
          }
        },
        actionText: 'Chiudi',
      };
      errorManager.handleError(ErrorService.getSeverityErrorHigh(), errorGeneric);
      return;
    }
    if (productRedirect?.slug) {
      if (isShoppingCart) {
        const querySlug = paths[paths.length - 2];
        if (querySlug && querySlug == productRedirect?.slug) {
          reload();
        }
        return;
      }
      if (!ENABLE_DEBUG) {
        window.location.href = getRedirectCoverage();
      } else {
        const enableRedirect = window.confirm(`Redirect to shopping-cart?`);
        if (enableRedirect) {
          window.location.href = getRedirectCoverage();
        }
      }
    } else if (customRedirect) {
      customRedirect();
    }
  };

  const showBestCoverageFastweb = () => {
    const addressToDisplay = [
      address?.street || ' ',
      address?.streetNumber || ' ',
      '-',
      address?.city || ' ',
    ];
    const pageProduct = {
      product_name: product?.slug,
      product_price: product?.price && removeCurrency(product.price),
      product_id: `${product?.offerId}`,
      product_quantity: '1',
      product_category: ['fixed'],
    };
    const foundedFastwebTechnology = getFWTechByName(
      technologiesFastwebCMS,
      bestTech?.BestTechTechnology
    );
    trackView({
      event_name: 'tool_end',
      event_category: 'tools',
      event_label_track: 'fw coverage',
      page_type: 'coverage result',
      page_section: 'fixed offers',
      page_subsection: 'coverage tool',
      journey_type: 'journey',
      journey_name: 'coverage tool',
      product_target_segment: 'consumer',
      ...(bestTech?.BestTechTechnology && { product_technology: bestTech?.BestTechTechnology }),
      ...pageProduct,
    });
    offersProposalManager.handleOffersProposal({
      showModal: true,
      enableCloseClickOutside: false,
      isShowClose: false,
      topLogo: blackbox?.onlyFw?.logoModal || '',
      title: blackbox?.onlyFw?.titleModal || '',
      description: blackbox?.onlyFw?.descriptionModal || '',
      bottomDescription: blackbox?.onlyFw?.editAddressDescriptionModal?.replace(
        '$ADDRESS',
        addressToDisplay?.join(' ')
      ),
      bottomDescriptionAction: () => {
        setSlidesGoBack(1);
      },
      bottomDescriptionActionLabel: blackbox?.onlyFw?.editAddressActionLabel || '',
      firstCard: {
        cornerColor: '#FDC400',
        price: foundedFastwebTechnology?.price?.price || '',
        recurrence: foundedFastwebTechnology?.price?.recurrence || '',
        subprice: foundedFastwebTechnology?.price?.subprice || '',
        cornerImage: blackbox?.onlyFw?.cardFastweb?.logo || '',
        items: foundedFastwebTechnology?.fastwebBlackBox?.elements?.map(
          (e) =>
            e.text?.replace(
              '$MAXDOWNLOAD',
              convertMbToGb(bestTech?.bestTechMaxDownloadSpeed || '')
            ) || ''
        ),
      },
      firstCardAction: {
        label: blackbox?.onlyFw?.cardFastweb?.labelButton || "Scopri l'offerta Fastweb",
        textColor: '#0D0D0D',
        backgroundColor: '#FDC400',
        action: () => {
          if (!bestTech?.checkSum || !bestTech?.eagonId) {
            errorManager.handleError(ErrorService.getSeverityErrorMedium(), {
              isButtonSticky: true,
              title: 'Ops',
              message: 'Si è verificato un errore, riprova in seguito.',
              actionText: 'Riprova',
              actionEvent: () => {
                window.location.reload();
              },
              disableTrack: true,
            });
            return;
          }
          setPostForm({
            ...postForm,
            isEnabled: true,
            params: {
              eagonId: {
                name: 'idCivicoEgon',
                value: bestTech?.eagonId,
              },
              checksum: {
                name: 'chk',
                value: bestTech?.checkSum,
              },
            },
          });
        },
      },
    });
  };

  useEffect(() => {
    setIsSuccess(isSuccess);
  }, [isSuccess]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (errorType === CoverageErrorType.NO_COVERAGE_VODAFONE_ONLY_FW) {
      showBestCoverageFastweb();
    }
  }, [errorType]);

  useEffect(() => {
    if (isError) {
      setIsAddressConfirmed(false);
    }
    setErrorCoverage({
      ...address,
      errorType,
    });
    setIsError(isError);
  }, [isError]);

  useEffect(() => {
    if (activeSlideIndex === 0) {
      setIsError(false);
      setIsLoading(false);
      setIsSuccess(false);
    }
  }, [activeSlideIndex]);

  useEffect(() => {
    if (slidesGoBack && slidesGoBack !== -1 && stepperRef?.current) {
      programmaticallyGoBackNext(slidesGoBack, 'BACK');
    }
  }, [slidesGoBack]);

  useEffect(() => {
    const organizedStepManual = organizeFlowsAppend(
      steps,
      isManual,
      ID_FLOWS.AUTO,
      MANUAL_FLOW.MANUAL_CARD
    );
    setSteps(organizedStepManual);
  }, [isManual]);

  useEffect(() => {
    LoggerInstance.debug('Set product', product);
    localStorage.setItem('tProd', JSON.stringify(product));
    setOfferId(product?.offerId?.toString() || '');
    setCoverageToolFlowType(getCoverageToolFlowType(product, cmsGlobalConfig));
    setProduct(product as IProduct);
    if (product?.isLockInProduct) {
      setIsStartFromLockIn();
      setStartFromLockInId(product.offerId.toString());
      sessionStorage.removeItem('uxfauthorization');
    } else {
      deleteIsStartFromLockin();
      deleteStartFromLockinId();
    }
  }, [product]);

  useEffect(() => {
    if (checkWindow()) {
      localStorage.setItem('hubPath', setHubPath(product?.slug || ''));
      /**
       * If browser back button was used, flush cache
       * This ensures that user will always see an accurate, up-to-date view based on their state
       * https://stackoverflow.com/questions/8788802/prevent-safari-loading-from-cache-when-back-button-is-clicked
       */
      window.onpageshow = (event) => {
        if (event.persisted) {
          window.location.reload();
          handleClose?.();
        }
      };
    }
  }, []);

  return (
    <>
      {ENABLE_DEBUG && <EnableDebug />}
      <CoverageToolModalContainer ref={nodeRef}>
        <StepperStateCards
          ref={stepperRef}
          steps={steps}
          onBack={handleClose}
          onClose={handleClose}
          onFinish={handleRedirect}
          blurredImage={backgroundImage}
          hideBackButton={hideBackButton() || isDisabledBackButton}
          onActiveSlideChange={setActiveSlideIndex}
          goBackLabel={goBackInStepper}
        />
        {postForm?.isEnabled && (
          <HiddenPostForm url={postForm?.url || ''} params={postForm?.params} />
        )}
      </CoverageToolModalContainer>
    </>
  );
};

export default CoverageToolModalContent;
